import { FormSelect } from 'react-bootstrap';
import {
  NewsWidget,
  LocalWidgetRouter,
  WidgetRegistry,
  HasWidgetPermissions,
  DefaultWidgetContainerContext,
  WidgetViewModeProvider,
  WidgetHostContext,
  WidgetToolbar,
  WidgetChromeContext,
} from '@/core/widgets/export';
import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { useEffectivePermissions } from '@/core/permissions/export';

export default function SingleWidgetHost({
  leadingControls,
  headerControls,
  registry,
  includeDynamicWidgets = false,
  context = 'dashboard',
  headerVariant = 'select',
  onActiveWidgetChange,
}: {
  /** Rendered before the widget's own filter/ellipsis toolbar (e.g. a test-env badge). */
  leadingControls?: JSX.Element;
  headerControls: JSX.Element;
  registry: WidgetRegistry;
  includeDynamicWidgets?: boolean;
  context?: WidgetChromeContext;
  /** `'select'` (default) shows the widget-switching dropdown, as today. `'title'` shows the widget's own icon+title instead, for hosts that switch widgets some other way (e.g. a mobile widget selector page). */
  headerVariant?: 'select' | 'title';
  /** Called whenever the visible widget changes, in addition to the existing `localStorage['mobile-widget']` persistence. */
  onActiveWidgetChange?: (widget: NewsWidget) => void;
}) {
  const permissions = useEffectivePermissions();

  const inactiveWidgets = useMemo(() => {
    const staticWidgets = registry.getAll();

    if (includeDynamicWidgets) {
      const dynamicWidgetFactories = registry.getFactories();
      const dynamicWidgets: NewsWidget[] = [];

      // A factory's own getListableUrls() tree can include entries whose URL
      // actually belongs to a DIFFERENT registered factory's template (e.g.
      // SingleCalendarWidgetFactory lists some "Auction Calendars" entries
      // that are really PinnedSeriesWidgetFactory URLs) — so each entry must
      // be matched against every registered factory, not just the one whose
      // list it was found in.
      const processMenuEntries = (entries: any[]) => {
        entries.forEach(entry => {
          if (entry.url) {
            const matchingFactory = dynamicWidgetFactories.find(f => f.matchesTemplate(entry.url));

            if (matchingFactory) {
              try {
                const widget = matchingFactory.createInstance(entry.url, entry.name);

                dynamicWidgets.push(widget);
              } catch (error) {
                console.warn(`Failed to create widget for URL: ${entry.url}`, error);
              }
            } else {
              console.warn(`No registered factory matches URL: ${entry.url}`);
            }
          } else if (entry.children) {
            processMenuEntries(entry.children);
          }
        });
      };

      dynamicWidgetFactories.forEach(factory => {
        const listableUrls = factory.getListableUrls();
        if (listableUrls.length > 0) {
          processMenuEntries(listableUrls);
        }
      });

      return [...staticWidgets, ...dynamicWidgets];
    }

    return staticWidgets;
  }, [registry, includeDynamicWidgets]);
  const getDefaultWidget = () => {
    const allWidgets = [...inactiveWidgets];

    const widgetId = localStorage.getItem('mobile-widget');
    if (widgetId) {
      const savedWidget = registry.getById(widgetId);

      if (savedWidget) return savedWidget;
    }

    return allWidgets[0];
  };

  const [selectedWidget, setSelectedWidget] = useState<NewsWidget>(getDefaultWidget());
  const [initialPath, setInitialPath] = useState<string>();
  const [widgetPath, setWidgetPath] = useState<string>();
  const host = useContext(WidgetHostContext);

  const selectWidget = (event: ChangeEvent<HTMLSelectElement>) => {
    // eslint-disable-next-line
    const widget = inactiveWidgets.find(value => value.id == event.target.value);

    if (widget) {
      setSelectedWidget(widget);
      setInitialPath(undefined);
    }
  };

  useEffect(() => {
    console.log('selected widget', selectedWidget.id);
    localStorage.setItem('mobile-widget', selectedWidget.id);
    onActiveWidgetChange?.(selectedWidget);
    // eslint-disable-next-line
  }, [selectedWidget]);

  useEffect(() => {
    if (selectedWidget.id) {
      ReactGA.send({
        hitType: 'pageview',
        page: `/mobile/~${selectedWidget.id}`,
        title: selectedWidget.defaultTitle,
      });
    }
  }, [selectedWidget]);

  useEffect(() => {
    return host.registerActiveWidgetSelector((id, path) => {
      const widget = inactiveWidgets.find(value => value.id === id);
      if (!widget) return;
      setSelectedWidget(widget);
      setInitialPath(path);
    });
  }, [host, inactiveWidgets]);

  return (
    <WidgetViewModeProvider key={selectedWidget.id} widgetId={selectedWidget.id}>
      <div
        className={`d-flex h-100 flex-column${headerVariant === 'title' ? ' pt-0 pb-1 px-1' : ' p-1'}`}
      >
        <DefaultWidgetContainerContext widget={selectedWidget}>
          <div
            className={`d-flex align-items-center${headerVariant === 'title' ? '' : ' m-1'}`}
            style={headerVariant === 'title' ? { paddingLeft: 12, paddingRight: 12 } : undefined}
          >
            <div className={'flex-grow-1 flex-shrink-1 me-1'}>
              {headerVariant === 'title' ? (
                <div className={'fw-bold lh-1'}>{selectedWidget.headerPanel}</div>
              ) : (
                <FormSelect
                  size={'sm'}
                  className={'mb-1'}
                  onChange={selectWidget}
                  value={selectedWidget.id}
                >
                  {inactiveWidgets
                    .filter(
                      value => !value.shouldHideToUser || !value.shouldHideToUser(permissions),
                    )
                    .map(value => (
                      <option value={value.id} key={value.id + 'sel'}>
                        {value.defaultTitle}
                      </option>
                    ))}
                </FormSelect>
              )}
            </div>

            <span className={'flex-shrink-0 d-flex align-items-center'}>
              {leadingControls}
              {headerControls}
              <WidgetToolbar
                widget={selectedWidget}
                context={context}
                widgetPath={widgetPath}
                compact={headerVariant === 'title'}
              />
            </span>
          </div>

          <div
            className={`overflow-auto flex-grow-1${headerVariant === 'title' ? ' mx-1 mb-1' : ' m-1'}`}
            style={headerVariant === 'title' ? { marginTop: 0 } : undefined}
          >
            <HasWidgetPermissions widget={selectedWidget}>
              <LocalWidgetRouter
                widget={selectedWidget}
                key={selectedWidget.id}
                path={initialPath}
                onNavigate={setWidgetPath}
              />
            </HasWidgetPermissions>
          </div>
        </DefaultWidgetContainerContext>
      </div>
    </WidgetViewModeProvider>
  );
}
